<script setup lang="ts">
import { computed, onMounted, onUnmounted } from 'vue';
import { onKeyStroke } from '@vueuse/core';

import { injectStrict } from '@apps/main/modules/helpers/injectStrict';
import { RecipeStateKey } from '@apps/main/modules/state/state';
import { isHotelIngredient } from '@apps/main/modules/recipe/ingredients';

import { Landing } from '@libs/trip-planner-interfaces';
import { isFeatureToggleActive } from '@libs/feature-toggle';

import ShareWidgetInit from '../../core/components/ShareWidgetInit.vue';
import TheHeader from '../../core/components/theHeader/TheHeader.vue';
import { useBrowserStore, useRouterStore } from '../../core/stores';
import { AutoCompleteType } from '../../modules/autoComplete';

import TripOverviewPageLayout from './TripOverviewPageLayout.vue';
import BuildYourTripLayout from './components/BuildYourTripLayout.vue';
import TripOverviewPanel from './components/tripItinerary/TripOverviewPanel.vue';
import BuildYourTripPanel from './components/BuildYourTripPanel.vue';
import TripOverviewModals from './components/TripOverviewModals.vue';
import TripOverviewPanels from './components/TripOverviewPanels.vue';
import TripOverviewMap from './components/TripOverviewMap.vue';
import TripOverviewNotifications from './components/TripOverviewNotifications.vue';
import {
  usePremadeTripStore,
  useTripOverviewPageStore,
  useTripOverviewModalsStore,
  useTripOverviewMapStore,
  useTripOverviewPanelsStore,
  useTripOverviewNotificationsStore,
  useTripSummaryStore,
} from './stores';
import ExperimentTripSummaryPanel from './components/tripSummary/ExperimentTripSummaryPanel.vue';
import TripSummaryPanel from './components/tripSummary/TripSummaryPanel.vue';
import TripPriceSummaryFooterMobile from './components/tripSummary/TripPriceSummaryFooterMobile.vue';
import { TripOverviewPanelType } from './models';

const tripOverviewPageStore = useTripOverviewPageStore();
const tripOverviewMapStore = useTripOverviewMapStore();
const tripOverviewModalsStore = useTripOverviewModalsStore();
const tripOverviewPanelsStore = useTripOverviewPanelsStore();
const tripOverviewNotificationsStore = useTripOverviewNotificationsStore();
const tripSummaryStore = useTripSummaryStore();
const premadeTripStore = usePremadeTripStore();
const routerStore = useRouterStore();
const browserStore = useBrowserStore();
const recipeState = injectStrict(RecipeStateKey);

const moblieFooterHeight = '80px';

const shouldShowSummaryMobileFooter = computed(
  () =>
    browserStore.isSmallerThanSmallMin &&
    tripOverviewPageStore.hasRecipeIngredients &&
    !tripSummaryStore.isTripSummaryVisible &&
    tripOverviewPanelsStore.tripOverviewPanelType === TripOverviewPanelType.None
);

function showInitialView(): void {
  if (!routerStore.isInitialLoad) {
    return;
  }

  const firstIngredient = recipeState.recipe.value[0];

  if (
    recipeState.recipe.value.length === 1 &&
    firstIngredient &&
    isHotelIngredient(firstIngredient) &&
    firstIngredient.subIngredients.length === 0
  ) {
    if (routerStore.landing === Landing.room) {
      tripOverviewPanelsStore.showMetaHotelDetails();
    }
  }

  routerStore.isInitialLoad = false;
}

onMounted(() => {
  showInitialView();
  premadeTripStore.tryToFetchPremadeTripDetails();
  tripOverviewModalsStore.openModalAutomatically();
  tripOverviewPageStore.initializeView();
});

onUnmounted(() => {
  tripOverviewModalsStore.closeTripOverviewModal();
  tripOverviewPanelsStore.closeTripOverviewPanel();
  tripOverviewNotificationsStore.clearNotifications();
});

onKeyStroke('p', (event) => {
  const correctKeyShortcutTriggered = event.ctrlKey || event.metaKey;
  if (
    tripSummaryStore.isEveryIngredientAvailable &&
    correctKeyShortcutTriggered
  ) {
    event.preventDefault();
    routerStore.navigateToTripOverviewPrint();
  }
});
</script>

<template>
  <TripOverviewModals />
  <TripOverviewPanels />

  <template v-if="tripOverviewPageStore.hasRecipeIngredients">
    <TripOverviewNotifications />
    <TripOverviewMap
      v-if="tripOverviewMapStore.shouldShowFullScreenMap"
      @close="tripOverviewMapStore.closeFullScreenDynamicTripMap()"
    />
    <ShareWidgetInit />

    <TripOverviewPageLayout
      :class="{ 'has-mobile-footer': shouldShowSummaryMobileFooter }"
    >
      <template #leftColumn>
        <TheHeader class="header" />
        <TripOverviewPanel />
      </template>
      <template #rightColumn>
        <ExperimentTripSummaryPanel
          v-if="isFeatureToggleActive('price-calendar')"
        />
        <TripSummaryPanel v-else />
      </template>
    </TripOverviewPageLayout>
  </template>

  <template v-else>
    <BuildYourTripLayout>
      <BuildYourTripPanel
        @build-your-trip="
          tripOverviewModalsStore.openDestinationSelectorModal(
            '',
            undefined,
            AutoCompleteType.Destination
          )
        "
      />
    </BuildYourTripLayout>
  </template>
  <TripPriceSummaryFooterMobile
    v-show="shouldShowSummaryMobileFooter"
    class="trip-summary-mobile-footer"
  />
</template>

<style lang="scss" scoped>
.header {
  padding: var(--spacing-24);
}

.has-mobile-footer {
  margin-bottom: v-bind(moblieFooterHeight);
}

.trip-summary-mobile-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  height: v-bind(moblieFooterHeight);
  background-color: var(--color-white);
}
</style>
